import {AnimatePresence, motion} from "framer-motion"
import {FC, PropsWithChildren, Suspense, useEffect, useState} from "react"
import {useLocation} from "react-router-dom"
import shadowRoot from "react-shadow"
import {ReactSVG} from "react-svg"

import {FeedbackButton, Loading} from "@frontend/components"
import {NewCaseButton} from "@frontend/components/new-case-button"
import {
  DesktopAndLargeDesktop,
  DesktopOnly,
  MobileOnly,
} from "@frontend/components/responsive"
import {IconButton} from "@frontend/components/ui/button"
import {iconsColor} from "@frontend/design"
import closeIcon from "@frontend/design/icons/close.svg"
import stylesheet from "@frontend/routes/globals.css?inline"
import {isRootPath} from "@frontend/routing"
import {useSession} from "@frontend/session"
import {activeThemeId} from "@frontend/themes/activeTheme"
import {brandLinks} from "@frontend/themes/brandLinks"
import {APP_ROOT_ID, SHADOW_HOST_ID} from "@frontend/utils/getAppRoot"
import {trpc} from "@frontend/utils/trpc"
import {useBooleanState} from "@frontend/utils/useBooleanState"
import {ProductTitleMark} from "@frontend/widget/ProductTitleMark"
import {widgetChrome} from "@frontend/widget/brandChrome"
import type {UiCase} from "@ri2/app/server/routers/getCasePageData"
import {css, cx} from "@styled-system/css"
import {hstack} from "@styled-system/patterns"

import {CaseHeader} from "./case-header"
import {AppContainerContext, Breadcrumb} from "./context"
import {Sidebar} from "./sidebar"
import {ToggleSidebarButton} from "./toggle-sidebar-button"
import {useTranslation} from "../i18n"

const HEADER_HEIGHT_MOBILE = 66
const HEADER_HEIGHT_DESKTOP = 76

const BORDER_RADIUS = 12

const SIDEBAR_COLLAPSED_WIDTH = 68
const SIDEBAR_EXPANDED_WIDTH_DESKTOP = 248

interface Props {
  onExit: () => void
}

export const AppContainer: FC<PropsWithChildren<Props>> = ({
  onExit,
  children,
}) => {
  useEffect(() => {
    const handleTouch = (e: TouchEvent): void => {
      // e.target is the shadow root, so get the "real" target with
      // composedPath()
      const target = e.composedPath()[0]

      const targetElement = target instanceof Element ? target : null

      // If a touch begins outside of an input and is not a button, blur the active
      // element. This is typical behavior on mobile and also solves
      // issues in mobile Safari where the keyboard pushes the
      // viewport partly off screen which causes nested scrollable
      // areas.
      if (
        targetElement &&
        !targetElement.matches('input[type="text"], textarea') &&
        !targetElement.closest("button")
      ) {
        console.log("Bluring active element")
        const element = document.activeElement as HTMLElement
        element?.blur()
      }
    }

    document.addEventListener("touchstart", handleTouch)

    return (): void => {
      document.removeEventListener("touchstart", handleTouch)
    }
  }, [])

  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([])

  const [caseModel, setCaseModel] = useState<UiCase | undefined>(undefined)

  const {userRcId} = useSession()
  // query the case summaries so that they are hopefully populated by
  // the time the sidebar is opened
  trpc.caseSummaries.useQuery({userRcId}, {suspense: false})

  const {
    state: isSidebarExpanded,
    toggle: onToggleSidebar,
    setFalse: onHideSidebar,
  } = useBooleanState(false)

  const onNavigate = (): void => {
    onHideSidebar()
  }

  const location = useLocation()
  const isRootPage = isRootPath(location.pathname)

  const t = useTranslation()

  return (
    <shadowRoot.div id={SHADOW_HOST_ID}>
      <>
        <style>{stylesheet}</style>
        <div
          onClick={onExit}
          className={css({
            position: "fixed",
            inset: 0,
            backgroundColor: {
              base: widgetChrome.appBackdrop,
              desktop: "rgba(0, 0, 0, 0.3)",
            },
            backdropFilter: {
              base: "unset",
              desktop: "blur(40px)",
            },
            zIndex: "appBackdrop",
          })}
        />
        <div
          id={APP_ROOT_ID}
          data-ri2-theme={activeThemeId}
          className={css({
            position: "fixed",
            zIndex: "appModal",
            alignItems: "center",
            justifyContent: "center",
            inset: 0,
            borderTopRadius: 12,
            fontFamily: widgetChrome.fontFamily,
            desktop: {
              inset: 20,
              borderRadius: BORDER_RADIUS,
              overflow: "hidden",
              display: "flex",
              justifyContent: "flex-start",
              flexGrow: 1,
            },
          })}
        >
          <Sidebar
            expanded={isSidebarExpanded}
            onToggle={onToggleSidebar}
            onNavigate={onNavigate}
            case={caseModel}
            css={cx(
              css({
                position: {
                  base: "absolute",
                  largeDesktop: "relative",
                },
                top: 0,
                left: 0,
                bottom: 0,
                zIndex: "sidebar",

                display: "flex",
                height: "100%",
                largeDesktop: {
                  borderBottomLeftRadius: BORDER_RADIUS,
                },
              }),
              isSidebarExpanded
                ? css({
                    minWidth: {
                      base: "85%",
                      largeDesktop: SIDEBAR_EXPANDED_WIDTH_DESKTOP,
                    },
                  })
                : css({
                    minWidth: {
                      base: 0,
                      largeDesktop: SIDEBAR_COLLAPSED_WIDTH,
                    },
                  }),

              isSidebarExpanded
                ? css({
                    width: {
                      base: "85%",
                      largeDesktop: SIDEBAR_EXPANDED_WIDTH_DESKTOP,
                    },
                  })
                : css({
                    width: {
                      base: 0,
                      largeDesktop: SIDEBAR_COLLAPSED_WIDTH,
                    },
                  }),
            )}
          />
          <div
            className={cx(
              css({
                display: "flex",
                flexDirection: "column",
                textStyle: "body",
                height: "100%",
                flexGrow: 1,
                borderTopRadius: BORDER_RADIUS,
                backgroundColor: "background.primary",
                desktop: {
                  borderTopLeftRadius: 0,
                  borderRightRadius: BORDER_RADIUS,
                  borderLeftRadius: 0,
                },
              }),
            )}
          >
            <header
              className={css(
                {
                  position: "relative",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  height: HEADER_HEIGHT_MOBILE,
                  paddingX: 16,
                  gap: 16,
                  backgroundColor: "white",
                  paddingTop: 16,
                  paddingBottom: 8,
                  boxSizing: "border-box",

                  desktopOnly: {
                    maxWidth: "calc(100vw - 64px)",
                  },

                  desktop: {
                    height: HEADER_HEIGHT_DESKTOP,
                    paddingY: 6,
                    paddingX: 0,
                    gap: 20,
                    marginRight: 16,
                  },
                },
                isSidebarExpanded
                  ? {
                      desktop: {
                        marginLeft: 32,
                      },
                    }
                  : {
                      desktop: {
                        marginLeft: 8,
                      },
                    },
              )}
            >
              {/* Logo themes: header mark on landing. Text brand (Bodewell):
                  landing uses the page hero title; header mark only on cases. */}
              {(widgetChrome.headerLogoSrc ? !caseModel : !!caseModel) && (
                <div
                  className={css({
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    alignItems: "center",
                    gap: 12,
                    // During an active case the title sits center on mobile —
                    // keep the brand mark for desktop only then.
                    display: caseModel
                      ? {base: "none", desktop: "flex"}
                      : "flex",

                    largeDesktop: {
                      marginLeft: -17,
                    },
                  })}
                >
                  {widgetChrome.headerLogoSrc ? (
                    <img
                      src={widgetChrome.headerLogoSrc}
                      alt={widgetChrome.productTitle}
                      className={css({
                        height: {
                          base: 44,
                          desktop: 46,
                        },
                      })}
                    />
                  ) : (
                    <ProductTitleMark
                      starSize={15}
                      className={css({
                        fontSize: {
                          base: 18,
                          desktop: 20,
                        },
                        fontWeight: 700,
                        lineHeight: 1.2,
                        letterSpacing: "0.01em",
                        whiteSpace: "nowrap",
                      })}
                    />
                  )}
                  {brandLinks.partnerLogoSrc && (
                    <>
                      <div
                        className={css({
                          width: "1px",
                          height: 24,
                          backgroundColor: "rgba(0,0,0,0.2)",
                        })}
                      />
                      <img
                        src={brandLinks.partnerLogoSrc}
                        alt={brandLinks.partnerLogoAlt ?? ""}
                        className={css({
                          height: {
                            base: 28,
                            desktop: 30,
                          },
                        })}
                      />
                    </>
                  )}
                </div>
              )}

              <MobileOnly>
                <ToggleSidebarButton onToggleSidebar={onToggleSidebar} />
              </MobileOnly>
              <div
                className={hstack({
                  gap: 8,
                })}
              >
                <DesktopOnly>
                  <ToggleSidebarButton onToggleSidebar={onToggleSidebar} />
                </DesktopOnly>
                {caseModel && <CaseHeader caseModel={caseModel} />}
                {caseModel && brandLinks.partnerLogoSrc && (
                  <DesktopAndLargeDesktop>
                    <div
                      className={css({
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        paddingLeft: 4,
                      })}
                    >
                      <div
                        className={css({
                          width: "1px",
                          height: 20,
                          backgroundColor: "rgba(0,0,0,0.15)",
                        })}
                      />
                      <img
                        src={brandLinks.partnerLogoSrc}
                        alt={brandLinks.partnerLogoAlt ?? ""}
                        className={css({
                          height: 26,
                        })}
                      />
                    </div>
                  </DesktopAndLargeDesktop>
                )}
              </div>
              <div
                className={hstack({
                  gap: 8,
                  height: 44,
                  alignItems: "center",
                })}
              >
                {!isRootPage && (
                  <>
                    <DesktopAndLargeDesktop>
                      <FeedbackButton css={css.raw({flexShrink: 0})} />
                    </DesktopAndLargeDesktop>
                    <DesktopAndLargeDesktop>
                      <NewCaseButton css={css.raw({flexShrink: 0})} />
                    </DesktopAndLargeDesktop>
                  </>
                )}
                <div
                  className={css({
                    height: 36,
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    minWidth: "fit-content",
                    flex: 0,
                  })}
                >
                  <div
                    className={hstack({
                      gap: 16,
                      minWidth: "fit-content",
                    })}
                  >
                    <IconButton
                      variant="unstyled"
                      ariaLabel={t("general.close")}
                      onClick={onExit}
                      css={css.raw({
                        "& svg": {width: 24, height: 24},
                        width: 44,
                        height: 44,
                      })}
                      icon={
                        <ReactSVG
                          src={closeIcon}
                          className={iconsColor({
                            color: "black",
                          })}
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            </header>
            <AnimatePresence initial={false}>
              {isSidebarExpanded && (
                <motion.div
                  onClick={onToggleSidebar}
                  initial="collapsed"
                  animate="open"
                  exit="collapsed"
                  variants={{
                    open: {opacity: 0.8},
                    collapsed: {opacity: 0},
                  }}
                  className={css({
                    display: {base: "block", largeDesktop: "none"},
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    backgroundColor: "black",
                    opacity: 0.8,
                    zIndex: "sidebarBackdrop",
                  })}
                />
              )}
            </AnimatePresence>

            <div
              className={css({
                flex: 1,
                position: "relative",
                display: "flex",
                flexDirection: "row",
                overflow: "hidden",
                margin: {
                  base: 0,
                  desktop: "0px 8px 0px 8px",
                  largeDesktop: "0px 8px 0px 0px",
                },
              })}
            >
              <div
                className={css({
                  flex: 1,
                  overflowY: "auto",
                })}
              >
                <AppContainerContext.Provider
                  value={{
                    breadcrumbs,
                    setBreadcrumbs,
                    setCase: setCaseModel,
                    isSidebarExpanded,
                    onToggleSidebar,
                  }}
                >
                  <Suspense
                    fallback={
                      <Loading
                        className={css({width: "100%", height: "100%"})}
                      />
                    }
                  >
                    {children}
                  </Suspense>
                </AppContainerContext.Provider>
              </div>
            </div>
          </div>
        </div>
      </>
    </shadowRoot.div>
  )
}
